import React, { useState, useCallback, useEffect } from "react";
import { Card, Table, Button, Space, Tag, Modal, message, Tooltip, Dropdown, Badge, Typography, Spin, Tabs } from "antd";
import { PlusOutlined, EditOutlined, MoreOutlined, StarOutlined, StarFilled, EyeOutlined, ExportOutlined, DownOutlined, UnorderedListOutlined, NodeIndexOutlined } from "@ant-design/icons";

import { useTableSelection } from "../../../hooks";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import api from "../../../api/enhancedApi";
import { formatDate } from "../../../utils/dateUtils";
import FlightRouteFilter from "./components/FlightRouteFilter";
import FlightRouteForm from "./components/FlightRouteForm";
import RouteCombinationFinder from "./components/RouteCombinationFinder";

const { Text } = Typography;
const { confirm } = Modal;

/**
 * 航路管理列表页面 - 符合IATA/ICAO标准
 */
const FlightRouteList = () => {
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 视图切换：航路列表 / 航路组合
    const [viewMode, setViewMode] = useState("routes");

    const [filterParams, setFilterParams] = useState({});
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [formLoading, setFormLoading] = useState(false);

    // 图片预览状态
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);

    // 表单引用
    const formRef = React.useRef(null);

    // 列表数据管理
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    // 确保routes是数组
    const routes = data?.items || [];

    // 加载数据
    const loadData = useCallback(
        async (params = {}) => {
            setLoading(true);
            try {
                const queryParams = {
                    page: pagination.current,
                    page_size: pagination.pageSize,
                    ...filterParams,
                    ...params,
                };

                const response = await api.flightRoute.getFlightRoutes(queryParams);
                setData(response.data);
                setPagination((prev) => ({
                    ...prev,
                    total: response.data?.total || 0,
                }));
            } catch (error) {
                console.error("加载航路数据失败:", error);
                message.error("加载数据失败");
            } finally {
                setLoading(false);
            }
        },
        [filterParams, pagination.current, pagination.pageSize],
    );

    // 重新加载数据
    const reload = useCallback(() => {
        loadData();
    }, [loadData]);

    // 处理表格变化
    const handleTableChange = useCallback((newPagination, filters, sorter) => {
        const newPage = newPagination.current;
        const newPageSize = newPagination.pageSize;

        setPagination((prev) => ({
            ...prev,
            current: newPage,
            pageSize: newPageSize,
        }));

        // 立即加载新页面的数据
        loadData({
            page: newPage,
            page_size: newPageSize,
        });
    }, []);

    // 初始加载和筛选参数变化时重新加载
    useEffect(() => {
        loadData({
            page: 1,
            page_size: pagination.pageSize,
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [filterParams]);

    // 组件挂载时初始加载
    useEffect(() => {
        loadData();
    }, []);

    // 删除功能
    const handleDelete = useCallback(
        (id, name) => {
            if (!hasPermission("flight_route:write")) {
                message.error("您没有删除航路的权限");
                return;
            }

            confirm({
                title: "确认删除",
                content: `确定要删除航路「${name}」吗？已关联该航路的航班将失去航路绑定。`,
                okText: "删除",
                okType: "danger",
                cancelText: "取消",
                onOk: async () => {
                    try {
                        await api.flightRoute.deleteFlightRoute(id);
                        message.success(`航路 ${name} 删除成功`);
                        reload();
                    } catch (error) {
                        console.error("删除航路失败:", error);
                        message.error("删除失败");
                    }
                },
            });
        },
        [reload, hasPermission],
    );

    // 表格选择
    const { selectedRowKeys, rowSelection, clearSelection, getIndexColumn, getRowProps } = useTableSelection({
        rowKey: "id",
        dataSource: routes,
        pagination,
    });

    const formatBH = (minutes) => {
        if (!minutes) return "-";
        return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}m`;
    };

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "航路名称",
            key: "route_name",
            width: 180,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {record.route_name}
                        {record.is_default && <StarFilled style={{ color: "#faad14", marginLeft: 6, fontSize: 12 }} />}
                    </div>
                    {record.route_type && <Tag size="small" style={{ marginTop: 2 }}>{record.route_type}</Tag>}
                    {record.overflight_countries?.length > 0 && (
                        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 2, lineHeight: 1.4 }}>
                            经 {record.overflight_countries.slice(0, 3).join("、")}
                            {record.overflight_countries.length > 3 && ` 等${record.overflight_countries.length}国`}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "出发",
            dataIndex: "departure_airport",
            key: "departure_airport",
            width: 75,
            render: (code) => <Tag color="blue">{code}</Tag>,
        },
        {
            title: "到达",
            dataIndex: "arrival_airport",
            key: "arrival_airport",
            width: 75,
            render: (code) => <Tag color="green">{code}</Tag>,
        },
        {
            title: <Tooltip title="Block Hours - 轮档时间"><span>BH</span></Tooltip>,
            dataIndex: "block_time_minutes",
            key: "block_time_minutes",
            width: 80,
            sorter: (a, b) => (a.block_time_minutes || 0) - (b.block_time_minutes || 0),
            render: (minutes) => <Text strong>{formatBH(minutes)}</Text>,
        },
        {
            title: <Tooltip title="Nautical Miles"><span>NM</span></Tooltip>,
            dataIndex: "nautical_miles",
            key: "nautical_miles",
            width: 70,
            sorter: (a, b) => (a.nautical_miles || 0) - (b.nautical_miles || 0),
            render: (nm) => nm ? <span>{Number(nm).toLocaleString()}</span> : "-",
        },
        {
            title: <Tooltip title="Required Fuel / Trip Fuel / FOD"><span>燃油信息</span></Tooltip>,
            key: "fuel_info",
            width: 145,
            render: (_, record) => {
                if (record.block_fuel == null && record.trip_fuel == null && record.fuel_over_destination == null) return "-";
                return (
                    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                        {record.block_fuel != null && <div><Text strong style={{ color: "#1890ff" }}>Reqd:</Text> {record.block_fuel}T</div>}
                        {record.trip_fuel != null && <div><Text strong>Trip:</Text> {Number(record.trip_fuel).toLocaleString()}kg</div>}
                        {record.fuel_over_destination != null && <div><Text strong>FOD:</Text> {Number(record.fuel_over_destination).toLocaleString()}kg</div>}
                    </div>
                );
            },
        },
        {
            title: "适配飞机",
            dataIndex: "compatible_aircraft",
            key: "compatible_aircraft",
            width: 130,
            render: (aircraftList) => {
                if (!aircraftList || aircraftList.length === 0) {
                    return <Tag color="blue" size="small">全部</Tag>;
                }
                return (
                    <div>
                        {aircraftList.slice(0, 2).map((aircraft) => (
                            <Tag key={aircraft} size="small" color="cyan" style={{ marginBottom: 2 }}>
                                {aircraft}
                            </Tag>
                        ))}
                        {aircraftList.length > 2 && (
                            <Tag size="small" color="processing">+{aircraftList.length - 2}</Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: "几月风",
            dataIndex: "wind_months",
            key: "wind_months",
            width: 100,
            render: (months) => {
                if (!months || months.length === 0) return "—";
                return [...months].sort((a, b) => a - b).join(",");
            },
        },
        {
            title: "标签",
            dataIndex: "tags",
            key: "tags",
            width: 120,
            render: (tags) =>
                tags && tags.length > 0 ? (
                    <div>
                        {tags.slice(0, 2).map((tag) => (
                            <Tag key={tag} size="small" color="purple" style={{ marginBottom: 2 }}>{tag}</Tag>
                        ))}
                        {tags.length > 2 && <Tag size="small" color="default">+{tags.length - 2}</Tag>}
                    </div>
                ) : "-",
        },
        {
            title: "状态",
            key: "status",
            width: 80,
            filters: [
                { text: "启用", value: true },
                { text: "停用", value: false },
            ],
            onFilter: (value, record) => record.is_active === value,
            render: (_, record) => (
                <Badge status={record.is_active ? "success" : "default"} text={record.is_active ? "启用" : "停用"} />
            ),
        },
        {
            title: "图片",
            dataIndex: "route_image_url",
            key: "route_image_url",
            width: 60,
            render: (imageUrl, record) =>
                imageUrl ? (
                    <div
                        className="route-image-cell"
                        onClick={(e) => { e.stopPropagation(); handleImagePreview(record.id); }}
                    >
                        <img src={imageUrl} alt="航路图" className="route-image-thumb" />
                        <div className="route-image-mask">
                            <EyeOutlined className="route-image-icon" />
                        </div>
                    </div>
                ) : null,
        },
        {
            title: "录入日期",
            dataIndex: "created_at",
            key: "created_at",
            width: 100,
            sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
            render: (text) => <span style={{ fontSize: 12, color: "#8c8c8c" }}>{formatDate(text)}</span>,
        },
        {
            title: "操作",
            key: "actions",
            width: 168,
            fixed: "right",
            render: (_, record) => (
                <Space size="small" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="查看">
                        <Button type="text" icon={<EyeOutlined />} onClick={() => handleView(record)} />
                    </Tooltip>
                    <PermissionGuard permission="flight_route:write">
                        <Tooltip title="编辑">
                            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        </Tooltip>

                        <Tooltip title={record.is_default ? "取消默认" : "设为默认"}>
                            <Button type="text" icon={record.is_default ? <StarFilled /> : <StarOutlined />} style={{ color: record.is_default ? "#faad14" : undefined }} onClick={() => handleSetDefault(record)} />
                        </Tooltip>

                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "toggle",
                                        label: record.is_active ? "停用" : "启用",
                                        onClick: () => handleToggleStatus(record),
                                    },
                                    {
                                        key: "delete",
                                        label: "删除",
                                        danger: true,
                                        onClick: () => handleDelete(record.id, `航路 ${record.route_name}`),
                                    },
                                ],
                            }}
                            trigger={["click"]}
                        >
                            <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 筛选处理
    const handleFilter = useCallback((params) => {
        setFilterParams(params);
    }, []);

    const handleResetFilter = useCallback(() => {
        setFilterParams({});
    }, []);

    // 表单操作
    const handleCreate = () => {
        // 检查创建权限
        if (!hasPermission("flight_route:write")) {
            message.error("您没有创建航路的权限");
            return;
        }
        setSelectedRoute(null);
        setIsEdit(false);
        setIsViewMode(false);
        setIsFormVisible(true);
    };

    const handleEdit = (record) => {
        // 检查编辑权限
        if (!hasPermission("flight_route:write")) {
            message.error("您没有编辑航路的权限");
            return;
        }
        setSelectedRoute(record);
        setIsEdit(true);
        setIsViewMode(false);
        setIsFormVisible(true);
    };

    // 查看详情：复用编辑抽屉的完整字段布局，只读展示
    const handleView = (record) => {
        setSelectedRoute(record);
        setIsEdit(false);
        setIsViewMode(true);
        setIsFormVisible(true);
    };

    const handleFormSubmit = async (values) => {
        setFormLoading(true);
        try {
            if (isEdit) {
                await api.flightRoute.updateFlightRoute(selectedRoute.id, values);
                message.success("航路更新成功");
            } else {
                await api.flightRoute.createFlightRoute(values);
                message.success("航路创建成功");
            }
            setIsFormVisible(false);
            reload();
        } catch (error) {
            message.error(isEdit ? "更新失败" : "创建失败");
        } finally {
            setFormLoading(false);
        }
    };

    const handleFormCancel = () => {
        setIsFormVisible(false);
        setSelectedRoute(null);
        setIsViewMode(false);
    };

    // 设置默认航路
    const handleSetDefault = (record) => {
        if (!hasPermission("flight_route:write")) {
            message.error("您没有设置默认航路的权限");
            return;
        }

        const leg = `${record.departure_airport} → ${record.arrival_airport}`;

        if (record.is_default) {
            confirm({
                title: "取消默认航路",
                content: `确定取消「${record.route_name}」作为 ${leg} 的默认航路吗？`,
                okText: "确定",
                cancelText: "取消",
                onOk: async () => {
                    try {
                        await api.flightRoute.setDefaultRoute({
                            departure_airport: record.departure_airport,
                            arrival_airport: record.arrival_airport,
                            route_id: record.id,
                        });
                        message.success("已取消默认航路");
                        reload();
                    } catch (error) {
                        message.error("操作失败");
                    }
                },
            });
        } else {
            confirm({
                title: "设为默认航路",
                content: `将「${record.route_name}」设为 ${leg} 的默认航路。同一航段只能有一条默认航路，原有的默认设置会被替换。`,
                okText: "确定",
                cancelText: "取消",
                onOk: async () => {
                    try {
                        await api.flightRoute.setDefaultRoute({
                            departure_airport: record.departure_airport,
                            arrival_airport: record.arrival_airport,
                            route_id: record.id,
                        });
                        message.success("默认航路设置成功");
                        reload();
                    } catch (error) {
                        message.error("设置默认航路失败");
                    }
                },
            });
        }
    };

    // 切换状态
    const handleToggleStatus = async (record) => {
        // 检查写权限
        if (!hasPermission("flight_route:write")) {
            message.error("您没有修改航路状态的权限");
            return;
        }

        try {
            await api.flightRoute.updateFlightRoute(record.id, {
                is_active: !record.is_active,
            });
            message.success(`航路已${record.is_active ? "停用" : "启用"}`);
            reload();
        } catch (error) {
            message.error("操作失败");
        }
    };

    // 批量操作
    const handleBatchOperation = async (operation) => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要操作的航路");
            return;
        }

        // 检查批量操作权限
        if (!hasPermission("flight_route:write")) {
            message.error("您没有批量操作航路的权限");
            return;
        }

        try {
            await api.flightRoute.batchOperation({
                ids: selectedRowKeys,
                operation,
            });
            message.success("批量操作成功");
            clearSelection();
            reload();
        } catch (error) {
            message.error("批量操作失败");
        }
    };

    // 处理图片预览
    const handleImagePreview = async (routeId) => {
        setPreviewVisible(true);
        setPreviewLoading(true);
        setPreviewImage("");

        try {
            // 调用 API 获取原图签名 URL
            const response = await api.flightRoute.getRouteImageUrl(routeId, false);
            if (response.data?.data?.image_url) {
                setPreviewImage(response.data.data.image_url);
            } else {
                message.error("获取图片失败");
                setPreviewVisible(false);
            }
        } catch (error) {
            console.error("获取原图签名 URL 失败:", error);
            message.error("加载原图失败");
            setPreviewVisible(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    // 关闭图片预览
    const handlePreviewClose = () => {
        setPreviewVisible(false);
        setPreviewImage("");
    };

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查 - 如果用户没有查看航路的权限，显示无权限提示
    if (!hasPermission("flight_route:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看航路管理的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    const routeListContent = (
        <>
            {/* 搜索过滤 */}
            <Card className="filter-card">
                <FlightRouteFilter onFilter={handleFilter} onReset={handleResetFilter} />
            </Card>

            {/* 选中项统计 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    已选择 <span className="selected-count">{selectedRowKeys.length}</span> 项
                    <Button type="link" onClick={clearSelection}>
                        清空
                    </Button>
                </div>
            )}

            {/* 数据表格 */}
            <Card className="data-card">
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={routes}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                    }}
                    onChange={handleTableChange}
                    rowSelection={hasPermission("flight_route:write") ? rowSelection : null}
                    scroll={{ x: 1500 }}
                    size="small"
                    rowClassName="flight-route-row"
                    onRow={(record, index) => {
                        const rowProps = getRowProps(record, index);
                        return {
                            ...rowProps,
                            onClick: () => handleView(record),
                            style: { cursor: "pointer" },
                        };
                    }}
                />
            </Card>
        </>
    );

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">航路管理</div>
                <div className="page-action-compact">
                    {viewMode === "routes" && (
                        <>
                            <PermissionGuard permission="flight_route:write">
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                                    新建航路
                                </Button>
                            </PermissionGuard>
                            <Tooltip title="导出功能开发中">
                                <Button icon={<ExportOutlined />} disabled>
                                    导出
                                </Button>
                            </Tooltip>
                            <PermissionGuard permission="flight_route:write">
                                {selectedRowKeys.length > 0 && (
                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: "activate",
                                                    label: "批量启用",
                                                    onClick: () => handleBatchOperation("activate"),
                                                },
                                                {
                                                    key: "deactivate",
                                                    label: "批量停用",
                                                    onClick: () => handleBatchOperation("deactivate"),
                                                },
                                                {
                                                    key: "delete",
                                                    label: "批量删除",
                                                    danger: true,
                                                    onClick: () => {
                                                        confirm({
                                                            title: "确认删除",
                                                            content: `确定要删除选中的 ${selectedRowKeys.length} 条航路吗？`,
                                                            onOk: () => handleBatchOperation("delete"),
                                                        });
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <Button>
                                            批量操作 <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                )}
                            </PermissionGuard>
                        </>
                    )}
                </div>
            </div>

            <Tabs
                activeKey={viewMode}
                onChange={setViewMode}
                size="large"
                items={[
                    {
                        key: "routes",
                        icon: <UnorderedListOutlined />,
                        label: "航路列表",
                        children: routeListContent,
                    },
                    {
                        key: "combinations",
                        icon: <NodeIndexOutlined />,
                        label: "航路组合",
                        children: (
                            <Card className="data-card">
                                <RouteCombinationFinder />
                            </Card>
                        ),
                    },
                ]}
            />

            {/* 航路表单抽屉 */}
            <FlightRouteForm ref={formRef} visible={isFormVisible} title={isViewMode ? `查看航路 - ${selectedRoute?.route_name || ""}` : isEdit ? "编辑航路" : "新建航路"} initialValues={selectedRoute} onSubmit={handleFormSubmit} onCancel={handleFormCancel} onClose={handleFormCancel} isEdit={isEdit} readOnly={isViewMode} loading={formLoading} hideActions={true} />

            {/* 图片预览 Modal */}
            <Modal
                open={previewVisible}
                title="航路图片预览"
                footer={null}
                onCancel={handlePreviewClose}
                width={800}
                centered
            >
                {previewLoading ? (
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载原图...</p>
                    </div>
                ) : (
                    previewImage && (
                        <img
                            src={previewImage}
                            alt="航路图片"
                            style={{
                                width: "100%",
                                maxHeight: "70vh",
                                objectFit: "contain",
                            }}
                        />
                    )
                )}
            </Modal>
        </div>
    );
};

export default FlightRouteList;
